import { Suspense, lazy, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { ReactFlowProvider } from '@xyflow/react'
import '@xyflow/react/dist/style.css'
import './assignment-canvas.css'
import { Loader2 } from 'lucide-react'
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog'
import type { RecoverRequest } from '../api'
import { useRecoverRequests } from '../hooks'
import { effectiveIsGo } from '../components/WaitGoState'

const VisualizationApp = lazy(() => import('@/features/visualization/_legacy/App'))
const AppStateProvider = lazy(() =>
    import('@/features/visualization/_legacy/context/AppStateContext').then((m) => ({
        default: m.AppStateProvider,
    }))
)

/**
 * The recovery canvas, read-only, for command and control.
 *
 * Same canvas the recovery screens use — not a copy. It already had a `readOnly` prop that
 * disables every editing path (onConnect, the palette drop targets, nodesConnectable), which
 * had never been passed by any caller; that is what makes this a variant rather than a fork.
 *
 * What readOnly did NOT allow was a context menu at all, because it assumed read-only meant
 * look-but-do-nothing. Here the whole point is to act on a node, so the canvas now accepts a
 * small set of assignment actions and renders a three-option menu when they are supplied.
 *
 * Scoped to ONE request. The canvas needs a main node to lay out from, and a request's rows all
 * belong to the same graph, so the first row serves — the same derivation the unassigned-request
 * detail page uses.
 */
/**
 * The canvas is a lazily-loaded chunk over a megabyte of graph code, and then lays out several
 * hundred nodes. Without something on screen the dialog opens to an empty rectangle and reads as
 * broken. The canvas has its own overlay for the data load; this covers the chunk load before it.
 */
function CanvasSpinner({ label }: { label: string }) {
    return (
        <div className="h-full flex flex-col items-center justify-center gap-3 text-sm text-muted-foreground">
            <Loader2 className="h-6 w-6 animate-spin" />
            {label}
        </div>
    )
}

export interface AssignmentCanvasActions {
    onViewRunbook: (objectID: string, scenario: number | null, forecastStartTime: string | Date | null) => void
    onAssignEngineer: (row: RecoverRequest) => void
    onViewLocation: (objectID: string) => void
}

export function AssignmentCanvasDialog({
    open, onOpenChange, requestID, requestLabel, rows, actions, allowBypass = false,
}: {
    open: boolean
    onOpenChange: (o: boolean) => void
    requestID: string
    /** What the request is called, as the filter names it. The UUID identifies it; it does not
     *  tell anyone which recovery they are looking at. */
    requestLabel?: string | null
    rows: RecoverRequest[]
    actions: AssignmentCanvasActions
    /** The queue's bypass policy, so the canvas and the table agree on what "Go" means. */
    allowBypass?: boolean
}) {
    const { t } = useTranslation()

    // Resolve a node back to its request row. The canvas hands back an object id; every action
    // below needs the row (scenario, forecast time, the recover-request id the assign dialog
    // writes against), and looking it up here keeps the canvas ignorant of assignment concerns.
    const rowByObject = useMemo(() => {
        const m = new Map<string, RecoverRequest>()
        for (const r of rows || []) if (r.objectID) m.set(String(r.objectID), r)
        return m
    }, [rows])

    // The top of the hierarchy is the request's highest-banded object — LayerID carries the
    // classification band, and the band IS the recovery order, so the largest is the thing
    // everything else is underneath. No picker: the request already determines the root.
    //
    // Fetched separately rather than read from `rows`, because the queue the screen shows is
    // paginated and filtered to unassigned work. The top object can easily be on page two or
    // already assigned, and rooting the canvas at whatever happened to be on screen is exactly
    // the bug that drew a single switch.
    const all = useRecoverRequests({ requestID, perPage: 500, page: 1 })
    // Same unwrapping the list page does — the response nests one level deeper than it looks.
    const allRows: RecoverRequest[] = useMemo(() => {
        const r: any = all.data
        if (!r) return []
        const arr = r.data?.data || r.data
        return Array.isArray(arr) ? arr : []
    }, [all.data])

    const mainNodeId = useMemo(() => {
        const pool = allRows.length > 0 ? allRows : rows
        let best: RecoverRequest | null = null
        for (const r of pool) {
            if (!r.objectID) continue
            if (best == null || (r.layerID ?? -1) > (best.layerID ?? -1)) best = r
        }
        return best?.objectID ?? null
    }, [allRows, rows])

    const scenarioId = rows[0]?.scenario != null ? String(rows[0].scenario) : '1'

    // ASSIGN or WAIT per object, keyed by object id so the canvas can look a node up directly.
    // Built from the same rule the queue uses, and from the same bypass policy, so a node cannot
    // say ASSIGN while its row says WAIT.
    const assignmentStatus = useMemo(() => {
        const out: Record<string, { canAssign: boolean; bypassableOutstanding: number }> = {}
        for (const r of (allRows.length > 0 ? allRows : rows)) {
            if (!r.objectID) continue
            out[String(r.objectID)] = {
                // effectiveIsGo, not a rule of our own. The database's WaitGo already assumes
                // bypasses ARE permitted, so the policy being off makes an object LESS startable,
                // not more. Reimplementing it here got that backwards and showed ASSIGN on
                // objects the table was calling WAIT.
                canAssign: effectiveIsGo(r, allowBypass),
                // Shown whatever the policy says, because the TABLE shows it either way and a
                // node contradicting its own row is worse than a number needing context. With
                // the policy off it reads as "this many could be worked around if you allowed
                // it", which is the question the toggle exists to answer.
                bypassableOutstanding: Number(r.bypassableOutstanding ?? 0),
            }
        }
        return out
    }, [allRows, rows, allowBypass])

    const canvasActions = useMemo(() => ({
        onViewRunbook: (_node: any, objectId: string) => {
            const r = rowByObject.get(objectId)
            actions.onViewRunbook(objectId, r?.scenario ?? null, r?.forecastStartTime ?? null)
        },
        onAssignEngineer: (_node: any, objectId: string) => {
            const r = rowByObject.get(objectId)
            // Without the row there is no recover-request to assign against, so do nothing
            // rather than open a dialog that cannot save.
            if (r) actions.onAssignEngineer(r)
        },
        onViewLocation: (_node: any, objectId: string) => actions.onViewLocation(objectId),
    }), [rowByObject, actions])

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent className="max-w-[95vw] w-[95vw] h-[90vh] flex flex-col p-0 gap-0">
                <DialogHeader className="px-5 py-3 border-b flex-shrink-0">
                    <DialogTitle className="text-base">
                        {t('recoverRequests.canvasForRequest', 'Dependency canvas — {{id}}', { id: requestLabel || requestID })}
                    </DialogTitle>
                    <p className="text-xs text-muted-foreground">
                        {t('recoverRequests.canvasReadOnlyNote',
                            'Read-only. Right-click an object to view its runbook, assign an engineer, or see where it is.')}
                    </p>
                </DialogHeader>

                <div className="assignment-canvas-host">
                    {mainNodeId ? (
                        <Suspense fallback={<CanvasSpinner label={t('assignment.canvasBuilding', 'Building the canvas…')} />}>
                            <AppStateProvider>
                                <ReactFlowProvider>
                                    <VisualizationApp
                                        scenario="recovery"
                                        view="dependency"
                                        objectId={mainNodeId || undefined}
                                        requestId={requestID}
                                        scenarioId={scenarioId}
                                        serviceId={scenarioId}
                                        // No picker: the request determines the root.
                                        showMainPalette={false}
                                        readOnly
                                        // App.jsx is untyped legacy JS, so TypeScript infers
                                        // this prop's type from its `null` default. The cast is
                                        // the JS/TS boundary, not a suppressed real error.
                                        assignmentActions={canvasActions as unknown as null}
                                        assignmentStatus={assignmentStatus as unknown as null}
                                    />
                                </ReactFlowProvider>
                            </AppStateProvider>
                        </Suspense>
                    ) : all.isLoading ? (
                        <CanvasSpinner label={t('common.loading', 'Loading…')} />
                    ) : (
                        <div className="h-full flex items-center justify-center text-sm text-muted-foreground">
                            {t('recoverRequests.noHierarchyForecast', 'No hierarchy forecast data for this request.')}
                        </div>
                    )}
                </div>
            </DialogContent>
        </Dialog>
    )
}
